/** Durable composer originals, scoped to the owned project and explicit chat. */
import type { ConsultationDocument } from '@/types/consultation';

const DATABASE = 'dobidobi-consultation-uploads-v1';
const RECORDS = 'records';
const FILES = 'files';
const STORAGE_WAIT_MS = 15_000;
export const CONSULTATION_UPLOAD_TTL = 24 * 60 * 60 * 1000;
export const CONSULTATION_UPLOAD_MAX_COUNT = 32;

export interface ConsultationUploadScope { ownerId: string; projectId: string; threadId: string | null }
export interface ConsultationUploadCheckpoint {
  fingerprint: string; byteSize: number; uploadId?: string; documentId?: string; putState?: 'completed' | 'unknown';
}
export interface ConsultationUploadRecord extends ConsultationUploadScope {
  id: string; uploadId: string; file: Blob; fileName: string; lastModified: number;
  checkpoint?: ConsultationUploadCheckpoint; readyDocument?: ConsultationDocument;
  createdAt: number; updatedAt: number; cancelled?: boolean;
}
export interface ConsultationUploadPatch {
  checkpoint?: ConsultationUploadCheckpoint | null; readyDocument?: ConsultationDocument | null;
}
type Metadata = Omit<ConsultationUploadRecord, 'file'> & { scopeId: string; byteSize: number; fileType: string };
type StoredFile = { id: string; file: Blob };

export class ConsultationUploadRecoveryError extends Error {
  readonly code = 'upload_recovery_unavailable';
  readonly retryable = true;
  constructor() { super('upload_recovery_unavailable'); }
}
function scopeId(ownerId: string, projectId: string, threadId: string | null): string {
  if (!ownerId || !projectId || !(threadId === null || typeof threadId === 'string' && threadId.length > 0)) {
    throw new ConsultationUploadRecoveryError();
  }
  return JSON.stringify([ownerId, projectId, threadId]);
}
export function consultationUploadRecordId(ownerId: string, projectId: string, threadId: string | null, uploadId: string): string {
  scopeId(ownerId, projectId, threadId);
  if (!uploadId) throw new ConsultationUploadRecoveryError();
  return JSON.stringify([ownerId, projectId, threadId, uploadId]);
}
function identity(id: string): ConsultationUploadScope & { uploadId: string } {
  try {
    const values = JSON.parse(id);
    if (!Array.isArray(values) || values.length !== 4 || typeof values[0] !== 'string' || typeof values[1] !== 'string'
        || typeof values[3] !== 'string' || consultationUploadRecordId(...values as [string, string, string | null, string]) !== id) throw new Error();
    return { ownerId: values[0], projectId: values[1], threadId: values[2], uploadId: values[3] };
  } catch { throw new ConsultationUploadRecoveryError(); }
}
function checkpoint(value: ConsultationUploadCheckpoint | null | undefined, byteSize: number): ConsultationUploadCheckpoint | undefined {
  if (value == null) return undefined;
  if (!/^[a-f0-9]{64}$/i.test(value.fingerprint) || value.byteSize !== byteSize
      || value.putState !== undefined && !['unknown', 'completed'].includes(value.putState)
      || value.uploadId !== undefined && (typeof value.uploadId !== 'string' || !value.uploadId)
      || value.documentId !== undefined && (typeof value.documentId !== 'string' || !value.documentId)) throw new ConsultationUploadRecoveryError();
  return { fingerprint: value.fingerprint, byteSize: value.byteSize,
    ...(value.uploadId ? { uploadId: value.uploadId } : {}), ...(value.documentId ? { documentId: value.documentId } : {}),
    ...(value.putState ? { putState: value.putState } : {}) };
}
function document(value: ConsultationDocument | null | undefined): ConsultationDocument | undefined {
  if (value == null) return undefined;
  // Persist identities and content metadata, never transient signed/blob URLs.
  const { document_id, filename, status, fingerprint, material_manifest, page_count, source_asset_id, format, mime_type, width, height } = value;
  return { document_id, filename, status, fingerprint, material_manifest, page_count, source_asset_id, format, mime_type, width, height };
}
async function database(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') throw new ConsultationUploadRecoveryError();
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    let retired = false;
    const fail = () => {
      retired = true; clearTimeout(timer);
      try { request.transaction?.abort(); } catch { /* The upgrade may already be terminal. */ }
      reject(new ConsultationUploadRecoveryError());
    };
    const timer = setTimeout(fail, STORAGE_WAIT_MS);
    request.onupgradeneeded = () => {
      if (retired) { request.transaction?.abort(); return; }
      request.result.createObjectStore(RECORDS, { keyPath: 'id' }).createIndex('scope', 'scopeId');
      request.result.createObjectStore(FILES, { keyPath: 'id' });
    };
    request.onsuccess = () => {
      clearTimeout(timer);
      if (retired) { request.result.close(); return; }
      resolve(request.result);
    };
    request.onerror = fail; request.onblocked = fail;
  });
}
async function transaction<T>(run: (records: IDBObjectStore, files: IDBObjectStore, finish: (value: T) => void,
  fail: (error?: Error) => void) => void): Promise<T> {
  const db = await database();
  try { return await new Promise<T>((resolve, reject) => {
    const tx = db.transaction([RECORDS, FILES], 'readwrite');
    let result: T; let failure: Error | undefined;
    const timer = setTimeout(() => {
      try { tx.abort(); reject(new ConsultationUploadRecoveryError()); }
      catch { /* An already-terminal transaction's queued event is authoritative. */ }
    }, STORAGE_WAIT_MS);
    tx.oncomplete = () => { clearTimeout(timer); resolve(result); };
    tx.onerror = tx.onabort = () => { clearTimeout(timer); reject(failure ?? new ConsultationUploadRecoveryError()); };
    const fail = (error?: Error) => { failure = error; tx.abort(); };
    try { run(tx.objectStore(RECORDS), tx.objectStore(FILES), value => { result = value; }, fail); }
    catch (error) { fail(error instanceof Error ? error : undefined); }
  }); } finally { db.close(); }
}
function retired(row: Metadata, now: number): boolean { return Boolean(row.cancelled) || now - row.createdAt > CONSULTATION_UPLOAD_TTL; }
function retire(records: IDBObjectStore, files: IDBObjectStore, row: Metadata, now: number): void {
  files.delete(row.id);
  records.put({ ...row, checkpoint: undefined, readyDocument: undefined, cancelled: true, updatedAt: now });
}
function hydrate(row: Metadata, files: IDBObjectStore, finish: (value: ConsultationUploadRecord) => void, fail: (error?: Error) => void): void {
  const request = files.get(row.id);
  request.onsuccess = () => {
    const stored = request.result as StoredFile | undefined;
    if (!stored || stored.file.size !== row.byteSize || stored.file.type !== row.fileType) { fail(); return; }
    const { scopeId: _scope, byteSize: _bytes, fileType: _type, ...record } = row;
    finish({ ...record, file: stored.file });
  };
}
/** Commit the original exactly once. Repeated admission never replaces its bytes or checkpoints. */
export async function putConsultationUploadRecord(record: ConsultationUploadRecord): Promise<void> {
  const now = Date.now();
  const id = consultationUploadRecordId(record.ownerId, record.projectId, record.threadId, record.uploadId);
  if (record.id !== id || !record.file || !Number.isFinite(record.createdAt) || record.createdAt > now
      || now - record.createdAt > CONSULTATION_UPLOAD_TTL || record.cancelled
      || !record.fileName || !Number.isFinite(record.lastModified)) throw new ConsultationUploadRecoveryError();
  const metadata: Metadata = { id, ...identity(id), scopeId: scopeId(record.ownerId, record.projectId, record.threadId),
    fileName: record.fileName, lastModified: record.lastModified, byteSize: record.file.size, fileType: record.file.type,
    createdAt: record.createdAt, updatedAt: now, checkpoint: checkpoint(record.checkpoint, record.file.size), readyDocument: document(record.readyDocument) };
  await transaction<void>((records, files, finish, fail) => {
    const request = records.index('scope').getAll(metadata.scopeId);
    request.onsuccess = () => {
      const all = request.result as Metadata[];
      const existing = all.find(row => row.id === id);
      if (existing) {
        if (retired(existing, now) || existing.fileName !== metadata.fileName || existing.lastModified !== metadata.lastModified
            || existing.byteSize !== metadata.byteSize || existing.fileType !== metadata.fileType) { fail(); return; }
        finish(undefined); return;
      }
      if (all.filter(row => !retired(row, now)).length >= CONSULTATION_UPLOAD_MAX_COUNT) { fail(); return; }
      for (const row of all) {
        if (row.cancelled && now - row.updatedAt > CONSULTATION_UPLOAD_TTL) { records.delete(row.id); files.delete(row.id); }
        else if (!row.cancelled && retired(row, now)) retire(records, files, row, now);
      }
      files.add({ id, file: record.file } satisfies StoredFile); records.add(metadata); finish(undefined);
    };
  });
}
/** Updating progress cannot write/replace the original, including after a reload. */
export async function updateConsultationUploadRecord(id: string, patch: ConsultationUploadPatch): Promise<void> {
  identity(id);
  await transaction<void>((records, _files, finish, fail) => {
    const request = records.get(id);
    request.onsuccess = () => {
      const row = request.result as Metadata | undefined;
      if (!row || retired(row, Date.now())) { fail(); return; }
      try {
        records.put({ ...row, updatedAt: Date.now(),
          ...('checkpoint' in patch ? { checkpoint: checkpoint(patch.checkpoint, row.byteSize) } : {}),
          ...('readyDocument' in patch ? { readyDocument: document(patch.readyDocument) } : {}) });
        finish(undefined);
      } catch (error) { fail(error instanceof Error ? error : undefined); }
    };
  });
}
export async function getConsultationUploadRecord(id: string): Promise<ConsultationUploadRecord | undefined> {
  identity(id);
  return transaction((records, files, finish, fail) => {
    const request = records.get(id);
    request.onsuccess = () => {
      const row = request.result as Metadata | undefined;
      if (!row || retired(row, Date.now())) {
        if (row && !row.cancelled) retire(records, files, row, Date.now());
        finish(undefined); return;
      }
      hydrate(row, files, finish, fail);
    };
  });
}
export async function listConsultationUploadRecords(ownerId: string, projectId: string, threadId: string | null): Promise<ConsultationUploadRecord[]> {
  const scope = scopeId(ownerId, projectId, threadId);
  return transaction((records, files, finish, fail) => {
    const request = records.index('scope').getAll(scope);
    request.onsuccess = () => {
      const now = Date.now(); const live: Metadata[] = [];
      for (const row of request.result as Metadata[]) {
        if (row.cancelled && now - row.updatedAt > CONSULTATION_UPLOAD_TTL) { records.delete(row.id); files.delete(row.id); }
        else if (retired(row, now)) { if (!row.cancelled) retire(records, files, row, now); }
        else live.push(row);
      }
      const result: ConsultationUploadRecord[] = [];
      if (!live.length) { finish(result); return; }
      for (const row of live) hydrate(row, files, value => {
        result.push(value);
        if (result.length === live.length) finish(result.sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id)));
      }, fail);
    };
  });
}
/** Retire even before admission commits: a late put/update cannot resurrect cancellation. */
export async function cancelConsultationUploadRecord(id: string): Promise<void> {
  const scope = identity(id);
  await transaction<void>((records, files, finish) => {
    const request = records.get(id);
    request.onsuccess = () => {
      const now = Date.now();
      const row = request.result as Metadata | undefined;
      retire(records, files, row ?? { id, ...scope, scopeId: scopeId(scope.ownerId, scope.projectId, scope.threadId),
        fileName: '', lastModified: 0, byteSize: 0, fileType: '', createdAt: now, updatedAt: now }, now);
      finish(undefined);
    };
  });
}
// Completion/removal also retains a fence against an in-flight checkpoint.
export const deleteConsultationUploadRecord = cancelConsultationUploadRecord;

/** Browser leases end on reload/crash; no stale timer can release another tab's work. */
export async function withConsultationUploadLease<T>(id: string, run: () => Promise<T>, ifAvailable = false,
  signal?: AbortSignal): Promise<T | undefined> {
  signal?.throwIfAborted();
  identity(id);
  if (typeof navigator === 'undefined' || !navigator.locks) throw new ConsultationUploadRecoveryError();
  // Web Locks forbids combining signal with ifAvailable. A queued acquisition
  // receives the signal; a nonblocking acquisition still checks cancellation
  // before its callback starts any upload work.
  const options: LockOptions = ifAvailable ? { ifAvailable: true } : { ...(signal ? { signal } : {}) };
  return navigator.locks.request(`consultation-upload:${id}`, options, lock => {
    signal?.throwIfAborted();
    return lock ? run() : undefined;
  });
}
